import type { SupportMessage, SupportPriority, SupportStatusValue, SupportTicket, SupportType } from '@/api/supportApi'

/**
 * The Support screen's pure view rules: labels, tones, the four-step progress
 * tracker, the inbox chips and search. Kept here (not in the components) so
 * they are tested — vitest runs no .tsx.
 */

export const STATUS_LABEL: Record<SupportStatusValue, string> = {
  new: 'Received',
  in_progress: 'In progress',
  waiting_on_customer: 'Waiting on you',
  waiting_on_vendor: 'Waiting on a vendor',
  scheduled: 'Scheduled',
  escalated_engineering: 'With engineering',
  resolved: 'Resolved',
  closed: 'Closed',
}

export type StatusTone = 'waiting' | 'progress' | 'done' | 'neutral'

export function statusTone(s: SupportStatusValue): StatusTone {
  if (s === 'waiting_on_customer') return 'waiting'
  if (s === 'resolved' || s === 'closed') return 'done'
  if (s === 'new') return 'neutral'
  return 'progress'
}

export const TYPE_OPTIONS: { value: SupportType; label: string; hint: string }[] = [
  { value: 'defect', label: "Something's broken", hint: 'An error, a screen that misbehaves' },
  { value: 'integration', label: 'Interface / integration', hint: 'Epic, LIS, instruments, printers' },
  { value: 'data_correction', label: 'Data needs fixing', hint: 'A wrong value that you cannot correct' },
  { value: 'service_request', label: 'Request a change', hint: 'Configuration, access, a new form' },
  { value: 'question', label: 'Question', hint: 'How do I…?' },
]

export const TYPE_LABEL: Record<SupportType, string> = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label])) as Record<SupportType, string>

export const PRIORITY_OPTIONS: { value: SupportPriority; label: string; hint: string }[] = [
  { value: 'P1', label: 'P1', hint: 'Production stopped' },
  { value: 'P2', label: 'P2', hint: 'Major, with a workaround' },
  { value: 'P3', label: 'P3', hint: 'Normal' },
  { value: 'P4', label: 'P4', hint: 'Low, or a question' },
]

const DONE = new Set<SupportStatusValue>(['resolved', 'closed'])

export type StepState = 'done' | 'current' | 'todo'
export interface ProgressStep {
  label: string
  state: StepState
}

/** Received → Title21 responded → (Waiting on you | In progress) → Resolved. */
export function progressSteps(t: Pick<SupportTicket, 'status' | 'sla'>): ProgressStep[] {
  if (DONE.has(t.status))
    return ['Received', 'Title21 responded', 'Worked on', STATUS_LABEL[t.status]].map((label) => ({ label, state: 'done' as const }))
  const responded = !!t.sla?.firstRespondedAt || (t.status !== 'new' && t.status !== 'in_progress')
  const working = t.status !== 'new'
  return [
    { label: 'Received', state: 'done' },
    { label: 'Title21 responded', state: responded ? 'done' : 'todo' },
    { label: t.status === 'waiting_on_customer' ? 'Waiting on you' : STATUS_LABEL[t.status === 'new' ? 'in_progress' : t.status], state: working ? 'current' : 'todo' },
    { label: 'Resolved', state: 'todo' },
  ]
}

export type InboxChip = 'mine' | 'all' | 'waiting' | 'open' | 'resolved'

export const CHIPS: { value: InboxChip; label: string }[] = [
  { value: 'mine', label: 'Mine' },
  { value: 'all', label: 'All of this tenant' },
  { value: 'waiting', label: 'Waiting on you' },
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
]

export function matchesChip(t: SupportTicket, chip: InboxChip): boolean {
  switch (chip) {
    case 'mine':
      return !!t.raisedByMe
    case 'all':
      return true
    case 'waiting':
      return t.status === 'waiting_on_customer'
    case 'open':
      return !DONE.has(t.status)
    case 'resolved':
      return DONE.has(t.status)
  }
}

export function matchesSearch(t: SupportTicket, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return t.key.toLowerCase().includes(needle) || String(t.number).includes(needle) || t.subject.toLowerCase().includes(needle)
}

const MIN = 60_000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

function span(ms: number): string {
  if (ms < MIN) return 'under a minute'
  if (ms < HOUR) return `${Math.floor(ms / MIN)} min`
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h`
  const d = Math.floor(ms / DAY)
  return `${d} day${d === 1 ? '' : 's'}`
}

/** "just now", "5 min ago", "3 h ago", "2 days ago". */
export function timeAgo(iso: string, now: number): string {
  const ms = now - new Date(iso).getTime()
  return ms < MIN ? 'just now' : `${span(ms)} ago`
}

/** "in 5 h", or "overdue by 2 h" once past. */
export function dueIn(iso: string, now: number): string {
  const ms = new Date(iso).getTime() - now
  return ms >= 0 ? `in ${span(ms)}` : `overdue by ${span(-ms)}`
}

/**
 * Title21 is waiting on this tenant: the status says so AND Title21 has the
 * last word. Smart Support leaves the status on waiting_on_customer after a
 * customer reply until an agent moves it, so after you answer, it is Title21's turn.
 */
export function waitingOnYou(status: SupportStatusValue, messages: Pick<SupportMessage, 'author'>[]): boolean {
  if (status !== 'waiting_on_customer') return false
  const last = [...messages].reverse().find((m) => m.author.kind !== 'system')
  return !last || last.author.kind !== 'customer'
}

/** A closed ticket takes no reply (Smart Support would open a follow-up instead). */
export function isClosedForReplies(s: SupportStatusValue): boolean {
  return s === 'closed'
}

/** What "It's fixed — let Title21 close it" posts (customers cannot resolve in Smart Support). */
export const FIXED_BODY = 'Confirmed fixed on our side — Title21 can close this ticket.'

/** The patient-data reminder on every free-text field going to Title21. */
export const PHI_REMINDER = 'No patient identifiers (names, MRNs, DINs).'

/** Why Support cannot be used right now, in words a user can act on. */
export const NOT_CONNECTED: Record<'not_configured' | 'tenant_not_mapped' | 'unreachable', string> = {
  not_configured: "Support isn't set up for this tenant yet. An administrator adds Title21 Smart Support under Integrations, with the key from Title21.",
  tenant_not_mapped: "Title21 hasn't linked this tenant yet. Ask Title21 Support to add it to your client.",
  unreachable: "Title21 Support can't be reached right now. Try again shortly.",
}
